import * as React from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { CurrencySelector } from '@/components/ui/CurrencySelector';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import type { Budget, CreateTransactionRequest } from '@/types';

interface RecurringTransactionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  budget: Budget;
  currencies: string[];
  onSubmit: (transactions: CreateTransactionRequest[]) => Promise<void>;
}

export function RecurringTransactionDialog({
  open,
  onOpenChange,
  budget,
  currencies,
  onSubmit,
}: RecurringTransactionDialogProps) {
  const [amount, setAmount] = React.useState('');
  const [currency, setCurrency] = React.useState(budget.currency);
  const [merchant, setMerchant] = React.useState('');
  const [category, setCategory] = React.useState('');
  const [note, setNote] = React.useState('');
  const [selectedDates, setSelectedDates] = React.useState<string[]>([]);
  const [calendarMonthIndex, setCalendarMonthIndex] = React.useState(0);
  const [error, setError] = React.useState('');

  const calendarDates = React.useMemo(() => {
    const start = new Date(`${budget.startDate}T00:00:00`);
    start.setMonth(start.getMonth() - 1);
    const end = new Date(`${budget.endDate}T00:00:00`);
    end.setMonth(end.getMonth() + 1);
    const dates: string[] = [];
    const cursor = new Date(start);
    while (cursor <= end) {
      dates.push(cursor.toISOString().split('T')[0]);
      cursor.setDate(cursor.getDate() + 1);
    }
    return dates;
  }, [budget.endDate, budget.startDate]);

  const calendarMonths = React.useMemo(() => {
    const groups: { key: string; label: string; dates: string[] }[] = [];
    calendarDates.forEach((date) => {
      const d = new Date(`${date}T00:00:00`);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      const label = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      const current = groups[groups.length - 1];
      if (!current || current.key !== key) {
        groups.push({ key, label, dates: [date] });
      } else {
        current.dates.push(date);
      }
    });
    return groups;
  }, [calendarDates]);

  const resetForm = React.useCallback(() => {
    setAmount('');
    setMerchant('');
    setCategory('');
    setNote('');
    setSelectedDates([]);
    setError('');
    setCalendarMonthIndex(0);
    setCurrency(budget.currency);
  }, [budget.currency]);

  React.useEffect(() => {
    setCurrency(budget.currency);
  }, [budget.currency]);

  React.useEffect(() => {
    setCalendarMonthIndex(0);
  }, [calendarMonths.length]);

  const toggleDate = (date: string) => {
    setSelectedDates((prev) =>
      prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date]
    );
  };

  const handleSubmit = async () => {
    const parsedAmount = parseFloat(amount);
    if (!parsedAmount || parsedAmount <= 0) {
      setError('Enter a valid amount.');
      return;
    }
    if (selectedDates.length === 0) {
      setError('Select at least one date.');
      return;
    }

    setError('');
    const transactions = selectedDates.map((date) => ({
      budgetId: budget.id,
      amount: parsedAmount,
      currency,
      date,
      merchant: merchant || undefined,
      category: category || undefined,
      note: note || undefined,
    }));

    await onSubmit(transactions);
    onOpenChange(false);
    resetForm();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        onOpenChange(nextOpen);
        if (!nextOpen) resetForm();
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Recurring Spends</DialogTitle>
          <DialogDescription>
            Select dates and apply the same transaction details to each one.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Input
                label="Amount"
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
              <div className="pt-6">
                <CurrencySelector
                  value={currency}
                  onChange={setCurrency}
                  currencies={currencies}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Merchant (optional)"
                value={merchant}
                onChange={(event) => setMerchant(event.target.value)}
              />
              <Input
                label="Category (optional)"
                value={category}
                onChange={(event) => setCategory(event.target.value)}
              />
            </div>
            <Input
              label="Note (optional)"
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>

          <div className="rounded-lg border border-input p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm font-medium text-foreground">
                {calendarMonths[calendarMonthIndex]?.label ?? 'Select dates'}
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setSelectedDates([])}
              >
                Clear dates
              </Button>
            </div>

            <div className="text-xs text-muted-foreground">
              Selected dates: {selectedDates.length}
            </div>

            {calendarMonths.length > 0 && (
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCalendarMonthIndex((prev) => Math.max(0, prev - 1))}
                  disabled={calendarMonthIndex === 0}
                  className="h-9 w-9 p-0"
                  aria-label="Previous month"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="m15 18-6-6 6-6" />
                  </svg>
                </Button>

                <div className="flex-1 space-y-2">
                  {(() => {
                    const month = calendarMonths[calendarMonthIndex];
                    if (!month) return null;
                    const firstDate = new Date(`${month.dates[0]}T00:00:00`);
                    const leadingBlanks = firstDate.getDay();
                    const days = Array.from({ length: leadingBlanks }).map((_, idx) => (
                      <span key={`blank-${month.key}-${idx}`} />
                    ));

                    return (
                      <>
                        <div className="grid grid-cols-7 gap-2 text-[11px] text-muted-foreground">
                          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                            <span key={`${month.key}-${day}`} className="text-center">
                              {day}
                            </span>
                          ))}
                        </div>
                        <div className="grid grid-cols-7 gap-2 text-sm">
                          {days}
                          {month.dates.map((date) => {
                            const day = new Date(`${date}T00:00:00`).getDate();
                            const selected = selectedDates.includes(date);
                            return (
                              <button
                                key={date}
                                type="button"
                                onClick={() => toggleDate(date)}
                                className={`h-8 w-8 rounded-md border text-xs transition-colors ${
                                  selected
                                    ? 'bg-primary text-primary-foreground border-primary'
                                    : 'border-input text-foreground hover:bg-muted'
                                }`}
                              >
                                {day}
                              </button>
                            );
                          })}
                        </div>
                      </>
                    );
                  })()}
                </div>

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setCalendarMonthIndex((prev) =>
                      Math.min(calendarMonths.length - 1, prev + 1)
                    )
                  }
                  disabled={calendarMonthIndex >= calendarMonths.length - 1}
                  className="h-9 w-9 p-0"
                  aria-label="Next month"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                </Button>
              </div>
            )}
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit}>Add recurring spends</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
